import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RocketIcon } from 'lucide-react';

interface PublishWorkflowModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  workflowName: string;
  isNewWorkflow?: boolean;
  onPublish: (options: PublishOptions, workflowName: string) => void;
}

export interface PublishOptions {
  mcpTool: boolean;
  api: boolean;
}

export const PublishWorkflowModal: React.FC<PublishWorkflowModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  workflowName,
  isNewWorkflow = false,
  onPublish,
}) => {
  const [mcpTool, setMcpTool] = useState(false);
  const [api, setApi] = useState(false);
  const [editableWorkflowName, setEditableWorkflowName] =
    useState(workflowName);

  // Sync editable name with prop when it changes
  useEffect(() => {
    setEditableWorkflowName(workflowName);
  }, [workflowName]);

  const handlePublish = () => {
    onPublish(
      {
        mcpTool,
        api,
      },
      isNewWorkflow ? editableWorkflowName : '',
    );
    setIsModalOpen(false);
    // Reset state
    setMcpTool(false);
    setApi(false);
  };

  const isPublishDisabled =
    (!mcpTool && !api) || (isNewWorkflow && !editableWorkflowName.trim());

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className='w-[416px] p-0 gap-0 fixed top-20 right-4 left-auto transform-none translate-x-0 translate-y-0 z-50'>
        <DialogHeader className='flex flex-row items-center justify-between space-y-0 p-6'>
          <DialogTitle className='text-base font-semibold'>
            Publish {isNewWorkflow ? 'New Workflow' : `'${workflowName}'`}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className='flex flex-col gap-6 p-6'>
          {/* Workflow Name Field - only show for new workflows */}
          {isNewWorkflow && (
            <div className='space-y-2'>
              <Label htmlFor='workflow-name' className='text-sm font-medium'>
                Workflow Name
              </Label>
              <Input
                id='workflow-name'
                value={editableWorkflowName}
                onChange={(e) => setEditableWorkflowName(e.target.value)}
                placeholder='Enter workflow name'
                className='w-full'
              />
            </div>
          )}

          <div className='flex flex-col gap-8'>
            {/* MCP Tool Option */}
            <div className='flex items-start space-x-2'>
              <Checkbox
                id='mcp-tool'
                checked={mcpTool}
                onCheckedChange={(checked) => setMcpTool(checked as boolean)}
                className='mt-0.5'
              />
              <div className='grid gap-1.5 leading-none'>
                <label
                  htmlFor='mcp-tool'
                  className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                >
                  MCP Tool
                </label>
                <p className='text-sm text-muted-foreground'>
                  Publish this workflow as an MCP tool for use in the workflow
                  composer or another application.
                </p>
              </div>
            </div>

            {/* API Option */}
            <div className='flex items-start space-x-2'>
              <Checkbox
                id='api'
                checked={api}
                onCheckedChange={(checked) => setApi(checked as boolean)}
                className='mt-0.5'
              />
              <div className='grid gap-1.5 leading-none'>
                <label
                  htmlFor='api'
                  className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                >
                  API
                </label>
                <p className='text-sm text-muted-foreground'>
                  Publish this workflow as an API to use in an application
                  outside of the Capitol AI platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        <DialogFooter className='p-6'>
          <Button
            onClick={handlePublish}
            disabled={isPublishDisabled}
            className='w-full'
          >
            <RocketIcon className='w-4 h-4 mr-2' />
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
